// no default React import needed with react-jsx
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Gavel as GavelIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { adminApi } from '../lib/sdkClient'
import { ModerationQueueItem, WaypointStatusResponse } from '../types/api'

export default function ModerationPage() {
  const [items, setItems] = useState<ModerationQueueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [selected, setSelected] = useState<WaypointStatusResponse | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const loadQueue = async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.adminModerationQueueArtrackAdminWaypointsModerationGet('pending', 50, 0)
      setItems((data as any)?.items || [])
      setError(null)
    } catch (e: any) {
      setError(e.message || 'Failed to load moderation queue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadQueue()
  }, [])
  // kept for future; currently not used
  // const getSeverityColor = (severity: string) => {
  //   switch (severity) {
  //     case 'high': return 'error'
  //     case 'medium': return 'warning'
  //     case 'low': return 'info'
  //     default: return 'default'
  //   }
  // }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <ErrorIcon />
      case 'medium': return <WarningIcon />
      case 'low': return <CheckCircleIcon />
      default: return <GavelIcon />
    }
  }

  const handleApprove = async (id: number) => {
    await adminApi.adminModerateWaypointArtrackAdminWaypointsWaypointIdModeratePost(id, 'approve')
    loadQueue()
  }

  const handleReject = async (id: number) => {
    await adminApi.adminModerateWaypointArtrackAdminWaypointsWaypointIdModeratePost(id, 'reject')
    loadQueue()
  }

  const handleEscalate = async (id: number) => {
    await adminApi.adminModerateWaypointArtrackAdminWaypointsWaypointIdModeratePost(id, 'quarantine')
    loadQueue()
  }

  const openPreview = async (waypointId: number) => {
    setPreviewOpen(true)
    setPreviewLoading(true)
    setSelected(null)
    setSelectedId(waypointId)
    try {
      // Fetch status via status endpoint for preview
      // Note: If not available, this can be adapted to fetch detail from track detail API
      const res = await fetch(`/api/artrack/waypoints/${waypointId}/status`)
      const details: WaypointStatusResponse = await res.json()
      setSelected(details)
    } catch (e) {
      // ignore; error banner already in page if needed
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Content Moderation
      </Typography>

      {/* Summary Cards (live counts) */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <GavelIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{items.length}</Typography>
                  <Typography color="textSecondary">Pending Items</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Moderation Queue */}
      <Typography variant="h6" gutterBottom>
        Pending Moderation
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      <Grid container spacing={2}>
        {items.map((item) => (
          <Grid item xs={12} key={item.id}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar sx={{ bgcolor: `warning.main` }}>
                  {getSeverityIcon('medium')}
                </Avatar>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h6">Inappropriate content detected</Typography>
                    <Chip label={item.moderation_status} color={'warning'} size="small" />
                    <Chip label={`waypoint #${item.id}`} variant="outlined" size="small" />
                  </Box>
                  
                  <Typography color="textSecondary" sx={{ mb: 2 }}>
                    AI flagged this waypoint for potentially inappropriate content. Click Preview to review the media and AI analysis.
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => openPreview(item.id)}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleApprove(item.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleReject(item.id)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleEscalate(item.id)}
                  >
                    Escalate
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Content Preview {selectedId ? `#${selectedId}` : ''}</DialogTitle>
        <DialogContent dividers>
          {previewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!previewLoading && selected && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Moderation: {selected.moderation_status} • State: {selected.processing_state}
              </Typography>
              {selected.media?.length > 0 ? (
                <ImageList cols={3} gap={8} sx={{ mb: 2 }}>
                  {selected.media.map((m) => (
                    <ImageListItem key={m.media_id}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={m.thumbnail_url || m.url} alt={`media ${m.media_id}`} loading="lazy" />
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <Alert severity="info">No media files attached.</Alert>
              )}
              {selected.media?.map((m) => (
                <Box key={`details-${m.media_id}`} sx={{ mb: 2 }}>
                  <Typography variant="body2">Type: {m.type} • State: {m.processing_state}</Typography>
                  {m.url && (
                    <Typography variant="body2">
                      URL: <a href={m.url} target="_blank" rel="noreferrer">Open</a>
                    </Typography>
                  )}
                  {m.analysis && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">AI Description: {m.analysis.description}</Typography>
                      <Typography variant="body2">Categories: {m.analysis.categories.join(', ')}</Typography>
                      <Typography variant="body2">Safety: {m.analysis.safety_rating} • Confidence: {m.analysis.confidence}</Typography>
                      <Typography variant="body2">Quality: {m.analysis.quality_score}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}